import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useParams } from 'react-router-dom';

import { createUrl, formatDate, timeMask, request } from 'app/utils';
import { ContentExternalDocumentTypeEnum, contentsTypes, ContentTypeEnum, useContents } from 'features/contents';

import { Flex, Section, Box, Typography, ButtonIcon, Loading, ConditionalLink } from 'components';
import { ModulesDetailsApiResponse, useModules } from 'features/modules';
import { maskTeachers } from 'features/teachers';
import { useSchool } from 'features/school';
import { AxiosResponse } from 'axios';
import { useProducts } from 'features/products';

const Wrapper = styled(Flex)``;

const linesStyle = css`
  display: block;
  width: 100vw;
  height: 2px;
  background: ${({ theme }) => theme.colors.gray_200};
  position: absolute;
  left: -10px;
  z-index: 5;
`;

const Lines = styled('i')`
  ${linesStyle}
  bottom: 0;
`;

const LessonBullet = styled('i')`
  background: white;
  position: absolute;
  z-index: 5;

  &::before {
    content: '';
    border-radius: 50%;
    border: 2px solid ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : props.theme.colors.gray_600)};
    background: ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : 'transparent')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 12px;
      height: 22px;
      border-radius: 11px;
      top: -2px;
      left: -29px;

      &::before {
        width: 12px;
        height: 12px;
      }

      ${mediaQueries.tablet} {
        width: 14px;
        height: 26px;
        border-radius: 13px;
        top: -3px;
        left: -29px;

        &::before {
          width: 14px;
          height: 14px;
        }
      }

      ${mediaQueries.desktop} {
        width: 16px;
        height: 30px;
        border-radius: 15px;
        top: -4px;
        left: -30px;

        &::before {
          width: 16px;
          height: 16px;
        }
      }
    `;
  }}
`;

const ContentLink = styled(ConditionalLink)`
  display: flex;
  flex-grow: 1;
  justify-content: column;
  z-index: 10;
  flex-wrap: nowrap;

  &::before {
    content: '';
    display: block;
    width: 2px;
    height: 100%;
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    top: 0;
    left: -23px;
  }
`;

const ContentSection = styled(Section)`
  position: relative;
`;

const HeaderSection = styled(Section)<{ schoolAlias: string }>`
  position: relative;

  &::after {
    content: '';
    display: block;
    background: ${({ theme, schoolAlias }) => (schoolAlias === 'b3' ? theme.colors.secondary_400 : theme.colors.primary_400)};
    position: absolute;
    border-radius: ${({ schoolAlias }) => (schoolAlias === 'b3' ? '20px' : 0)};
  }

  &::before {
    content: '';
    display: block;
    width: 2px;
    height: calc(100% - 23px);
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    bottom: 0;
    left: -23px;
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      &::after {
        width: 14px;
        height: 14px;
        left: -28px;
        top: 1px;
      }

      &::before {
        height: calc(100% - 20px);
      }

      ${mediaQueries.desktop} {
        &::after {
          width: 16px;
          height: 16px;
          left: -29px;
          top: 2px;
        }

        &::before {
          height: calc(100% - 23px);
        }
      }
    `;
  }}
`;

const ContentRow = styled(Flex)`
  position: relative;

  &:hover {
    &::after {
      content: '';
      display: block;
      width: 100vw;
      height: 100%;
      background: ${({ theme }) => theme.colors.gray_100};
      position: absolute;
      bottom: 0;
      left: 0;
    }

    ${LessonBullet} {
      background: ${({ theme }) => theme.colors.gray_100};
    }
  }

  &:first-child {
    ${ContentSection} {
      &::before {
        content: '';
        ${linesStyle}
        top: 0;
      }
    }
  }

  &:last-child {
    ${ContentLink} {
      &:before {
        height: 50%;
      }
    }
  }
`;

interface ContensListProps {
  productSlug?: string;
}

const ContensList = ({ productSlug: forceProductSlug }: ContensListProps): ReactElement => {
  const [loading, setLoading] = useState(true);
  const { productSlug, productClassUuid: receivedProductClassUuid, moduleUuid } = useParams();

  const { productDetails } = useProducts();
  const { moduleDetails } = useModules();
  const { setContentsList, clearContentsList, contentsList } = useContents();
  const { schoolAlias, apiGatewayUrl } = useSchool();

  const { setModuleDetails, clearModuleDetails } = useModules();

  const getProductSlug = useMemo(() => forceProductSlug || productSlug, [forceProductSlug, productSlug]);

  const getProductClassUuid = useCallback(() => {
    if (receivedProductClassUuid) return receivedProductClassUuid;

    if (moduleDetails?.productClassUuid) return moduleDetails.productClassUuid;

    return undefined!;
  }, [moduleDetails, receivedProductClassUuid]);

  const fetchContents = async () => {
    await Promise.all([
      request({
        url: `/api/v1/School/ProductContentTimeline/${getProductSlug}${moduleUuid ? `/${moduleUuid}` : ''}${
          getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
        }`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response: AxiosResponse<ModulesDetailsApiResponse>) => {
        setModuleDetails(response.data);
        setContentsList(response.data.items);
      }),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchContents();

    return () => {
      clearModuleDetails();
      clearContentsList();
    };
  }, []);

  if (loading) return <Loading label="Por favor, aguarde." />;

  return !!contentsList?.length ? (
    <Wrapper flexGrow={0} flexDirection="column" bg="white" pt={50} pb={100}>
      <HeaderSection schoolAlias={schoolAlias} flexDirection="column" pb={{ m: 20, d: 30 }}>
        <Typography type="type3" upper>
          {moduleDetails?.hat || productDetails?.product?.hat}
        </Typography>

        <Typography type="type6" fontWeight="bold" mt={2} mb={{ m: 30, d: 40 }}>
          {moduleDetails?.name || productDetails?.product?.name}
        </Typography>

        {Boolean(moduleDetails?.teachers?.length) && <Typography type="type3">Por: {maskTeachers(moduleDetails.teachers)}</Typography>}
      </HeaderSection>

      <Flex flexGrow={0} flexDirection="column">
        {contentsList.map((content, index) => (
          <ContentRow key={`content-${content.uuid}`} flexGrow={0}>
            <ContentSection py={{ m: 15 }}>
              <Lines />

              <ContentLink
                type={content.uuid ? 'internal' : 'nolink'}
                to={
                  getProductClassUuid()
                    ? createUrl(moduleUuid ? 'contentClassDetails' : 'productContentClassDetails', [
                        getProductSlug,
                        getProductClassUuid(),
                        moduleUuid,
                        content.uuid,
                        contentsTypes(content.contentType, content.externalDocumentType),
                      ])
                    : createUrl(moduleUuid ? 'contentDetails' : 'productContentDetails', [
                        getProductSlug,
                        moduleUuid,
                        content.uuid,
                        contentsTypes(content.contentType, content.externalDocumentType),
                      ])
                }
                component={Flex}
                data-testid={`module-content-list-content-${index.toString()}`}
              >
                <Flex flexGrow={1} alignItems="center" flexWrap="nowrap">
                  <Flex display="inline-flex" flexGrow={0} flexWrap="nowrap" alignItems="flex-start" position="relative">
                    <Typography type="type4" fontWeight="bold" style={{ whiteSpace: 'nowrap' }}>
                      <LessonBullet data-active={content.isComplete} />
                      {content.timelineOrder} / {contentsList.length}
                    </Typography>

                    <Typography type="type4" fontWeight="bold" mx={{ m: 1, t: 2 }}>
                      •
                    </Typography>

                    <Typography type="type4" fontWeight="bold">
                      {content.name}

                      {!Boolean(content.uuid) && Boolean(content.releaseDate) && (
                        <>
                          {' '}
                          <Box display={{ m: 'none', d: 'inline' }}>
                            <Typography type="type2" color="gray_500" mx={{ m: 2 }}>
                              •
                            </Typography>
                          </Box>
                          <Box display={{ m: 'block', d: 'inline' }}>
                            {content.showDate ? (
                              <Typography type="type3" color="gray_500">
                                {formatDate(content.releaseDate).format('DD/MM/YYYY')}
                              </Typography>
                            ) : (
                              <Typography type="type3" color="gray_500">
                                Em breve
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </Typography>
                  </Flex>
                </Flex>

                <Flex flexGrow={0} alignItems="center" flexWrap="nowrap" ml={{ m: 20, d: 30 }}>
                  {content.contentType === ContentTypeEnum.Video && (
                    <>
                      <Box display={{ m: 'none', d: 'inline' }} mr="10px">
                        {Boolean(content.videoCurrentPointInTime) && !content.isComplete ? (
                          <Typography type="type3" color={schoolAlias === 'b3' ? 'secondary_400' : 'primary_400'} fontWeight="bold" upper>
                            Continuar
                          </Typography>
                        ) : (
                          <>
                            {Boolean(content.uuid && content.durationSeconds) && (
                              <Typography type="type2" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                                {timeMask(content.durationSeconds)}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>

                      <ButtonIcon
                        icon="play"
                        width={{ m: 30, d: 50 }}
                        height={{ m: 30, d: 50 }}
                        iconWidth={{ m: 15, d: 20 }}
                        bg={content.isComplete ? 'gray_200' : 'transparent'}
                        bgHover={Boolean(content.uuid) ? (schoolAlias === 'b3' ? 'secondary_400' : 'primary_400') : 'transparent'}
                        color={
                          schoolAlias === 'b3'
                            ? Boolean(content.uuid)
                              ? content.isComplete
                                ? 'white'
                                : schoolAlias === 'b3'
                                ? 'secondary_400'
                                : 'primary_400'
                              : 'gray_300'
                            : Boolean(content.uuid)
                            ? content.isComplete
                              ? 'white'
                              : schoolAlias === 'b3'
                              ? 'secondary_400'
                              : 'primary_400'
                            : 'gray_200'
                        }
                        colorHover={
                          schoolAlias === 'b3' ? (Boolean(content.uuid) ? 'white' : 'gray_300') : Boolean(content.uuid) ? 'white' : 'gray_200'
                        }
                        borderWidth={content.isComplete ? 0 : '2px'}
                        borderColor={
                          schoolAlias === 'b3'
                            ? Boolean(content.uuid)
                              ? content.isComplete
                                ? 'gray_300'
                                : 'secondary_400'
                              : 'gray_300'
                            : Boolean(content.uuid)
                            ? content.isComplete
                              ? 'gray_200'
                              : 'primary_400'
                            : 'gray_200'
                        }
                        borderStyle="solid"
                        borderRadius="50%"
                        title="Assistir Vídeo"
                      />
                    </>
                  )}

                  {content.contentType === ContentTypeEnum.Article && (
                    <>
                      <Box display={{ m: 'none', d: 'inline' }}>
                        <Typography type="type2" color="gray_500" mr="10px" style={{ whiteSpace: 'nowrap' }}></Typography>
                      </Box>

                      <ButtonIcon
                        icon="text"
                        width={{ m: 30, d: 50 }}
                        height={{ m: 30, d: 50 }}
                        iconWidth={{ m: 25, d: 30 }}
                        bg="transparent"
                        color={
                          Boolean(content.uuid)
                            ? content.isComplete
                              ? 'gray_200'
                              : schoolAlias === 'b3'
                              ? 'secondary_400'
                              : 'primary_400'
                            : 'gray_200'
                        }
                        title="Ler Texto"
                      />
                    </>
                  )}

                  {content.contentType === ContentTypeEnum.Quiz && (
                    <>
                      <Box display={{ m: 'none', d: 'inline' }}>
                        <Typography type="type2" color="gray_500" mr="10px" style={{ whiteSpace: 'nowrap' }}>
                          {content.numberOfQuestions} questões
                        </Typography>
                      </Box>

                      <ButtonIcon
                        icon="quiz"
                        width={{ m: 30, d: 50 }}
                        height={{ m: 30, d: 50 }}
                        iconWidth={{ m: 25, d: 30 }}
                        bg="transparent"
                        color={
                          Boolean(content.uuid)
                            ? content.isComplete
                              ? 'gray_200'
                              : schoolAlias === 'b3'
                              ? 'secondary_400'
                              : 'primary_400'
                            : 'gray_200'
                        }
                        title="Responder Quiz"
                      />
                    </>
                  )}

                  {content.contentType === ContentTypeEnum.Survey && (
                    <>
                      <ButtonIcon
                        icon="commentQuestionOutline"
                        width={{ m: 30, d: 50 }}
                        height={{ m: 30, d: 50 }}
                        iconWidth={{ m: 25, d: 30 }}
                        bg="transparent"
                        color={
                          Boolean(content.uuid)
                            ? content.isComplete
                              ? 'gray_200'
                              : schoolAlias === 'b3'
                              ? 'secondary_400'
                              : 'primary_400'
                            : 'gray_200'
                        }
                        title="Responder Pesquisa"
                      />
                    </>
                  )}

                  {content.contentType === ContentTypeEnum.ExternalDocument && content.externalDocumentType === ContentExternalDocumentTypeEnum.PDF && (
                    <>
                      <Box display={{ m: 'none', d: 'inline' }}>
                        <Typography type="type2" color="gray_500" mr="10px" style={{ whiteSpace: 'nowrap' }}></Typography>
                      </Box>

                      <ButtonIcon
                        icon="filePdfBox"
                        width={{ m: 30, d: 50 }}
                        height={{ m: 30, d: 50 }}
                        iconWidth={{ m: 25, d: 30 }}
                        bg="transparent"
                        color={
                          Boolean(content.uuid)
                            ? content.isComplete
                              ? 'gray_200'
                              : schoolAlias === 'b3'
                              ? 'secondary_400'
                              : 'primary_400'
                            : 'gray_200'
                        }
                        title="Ver PDF"
                      />
                    </>
                  )}
                </Flex>
              </ContentLink>
            </ContentSection>
          </ContentRow>
        ))}
      </Flex>
    </Wrapper>
  ) : (
    <></>
  );
};

export default ContensList;
